import { format } from "date-fns";
import { useState } from "react";
import { Button, Loading, Typography } from "@/components/atoms";
import { useDeclineAccessMutation, useGetAccessListQuery } from "@/redux/apis/assistants/assistantsApi";
import { AccessLink } from "@/redux/apis/assistants/types";
import { addToast } from "@/utils";
import { ClientAvatar } from "../myClients/ClientAvatar";
import { EmptyStateGraphic } from "../myClients/EmptyStateGraphic";
import { formatRelativeShort } from "../myClients/helpers";
import { useClientScope } from "./ClientScopeContext";
import { RemoveAssistantModal } from "./assistants/RemoveAssistantModal";
import { ReviewRequestModal } from "./assistants/ReviewRequestModal";

const formatDate = (iso: string | null) => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : format(date, "d MMM yyyy");
};

/**
 * Settings › Assistants. Lists assistants who manage (or have requested to manage) an
 * account. Self-service (client portal, `accountId` undefined) and operator-managed
 * (`useClientScope().accountId` set) share the same flow: approve/decline pending requests + remove
 * established access. Operators are authorized (via can_manage_clients) to approve/decline on the
 * client's behalf, so both views expose the identical Review/Decline/Remove affordances. The scoped
 * `accountId` is threaded through the assistantsApi mutations so the operator acts on the client.
 */
export const SettingsAssistants = () => {
  // Operator-managed client when set; undefined in self-service (client portal).
  const { accountId } = useClientScope();
  const isManaged = !!accountId;

  const [reviewLink, setReviewLink] = useState<AccessLink | null>(null);
  const [removeLink, setRemoveLink] = useState<AccessLink | null>(null);

  const { data: pending = [], isLoading: pendingLoading } = useGetAccessListQuery({ type: "MANAGES_YOU", status: "PENDING", accountId });
  const { data: connected = [], isLoading: connectedLoading } = useGetAccessListQuery({
    type: "MANAGES_YOU",
    status: "CONNECTED",
    accountId,
  });
  const [declineAccess, { isLoading: isDeclining }] = useDeclineAccessMutation();

  // Operators decline on the managed client's behalf via the scoped `accountId` (undefined = self-service).
  const handleDecline = async (link: AccessLink) => {
    try {
      await declineAccess({ id: link.id, accountId }).unwrap();
      addToast("success", `Declined ${link.party.name}'s request`);
    } catch {
      addToast("danger", "Failed to decline request. Please try again.");
    }
  };

  if (pendingLoading || connectedLoading) return <Loading />;

  return (
    <div>
      <Typography variant="title" className="mb-1 block font-semibold text-neutral-black">
        Assistants
      </Typography>
      <Typography variant="paragraph" className="mb-6 block text-neutral-black">
        {isManaged
          ? "People who can manage this client's bookings, preferences, and account details on their behalf. You can approve, decline, or remove access on the client's behalf at any time."
          : "People who can manage your bookings, preferences, and account details on your behalf. You can remove access at any time."}
      </Typography>

      <div className="flex flex-col gap-4">
        {pending.length > 0 && (
          <div className="rounded-lg border border-neutral-mid-gray p-5">
            <Typography variant="title" className="mb-5 block font-semibold text-neutral-silver-gray">
              Pending Request{pending.length === 1 ? "" : "s"}
            </Typography>
            <div className="flex max-h-80 flex-col divide-y divide-neutral-mid-gray overflow-y-auto">
              {pending.map((link) => (
                <div key={link.id} className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-4">
                    <ClientAvatar name={link.party.name} avatar={link.party.avatar} size="md" />
                    <div className="min-w-0">
                      <Typography variant="action" className="block truncate font-semibold text-neutral-black">
                        {link.party.name}
                      </Typography>
                      <Typography variant="paragraph" className="block truncate text-neutral-silver-gray">
                        {link.party.email}
                        {formatRelativeShort(link.createdAt) && <span> • Requested {formatRelativeShort(link.createdAt)} ago</span>}
                      </Typography>
                    </div>
                  </div>
                  {/* Same actions in self-service and operator-managed views — operators act on the
                      client's behalf via the scoped accountId. */}
                  <div className="flex shrink-0 gap-3">
                    <Button
                      variant="custom"
                      size="sm"
                      className="bg-danger text-white hover:bg-danger-dark"
                      onClick={() => handleDecline(link)}
                      disabled={isDeclining}
                    >
                      Decline
                    </Button>
                    <Button variant="primary" size="sm" onClick={() => setReviewLink(link)}>
                      Review Request
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-lg border border-neutral-mid-gray p-5">
          {/* The empty card drops the section header and centers a neutral illustration; only show
              the "Has access…" heading once there are assistants to head. */}
          {connected.length > 0 && (
            <Typography variant="title" className="mb-5 block font-semibold text-neutral-silver-gray">
              {isManaged ? "Has access to this client's account" : "Has access to your account"}
            </Typography>
          )}

          {connected.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-4 py-14 text-center">
              <EmptyStateGraphic className="mb-2" />
              <Typography variant="action" className="mt-1 font-semibold text-neutral-black">
                {isManaged ? "No one has access to this client's account" : "No one has access to your account"}
              </Typography>
              <Typography variant="paragraph" className="max-w-xs text-neutral-dark-gray">
                {isManaged
                  ? "When an assistant requests access to manage this client's bookings, you'll see it here."
                  : "When an assistant requests access to manage your bookings on your behalf, you'll see it here."}
              </Typography>
            </div>
          ) : (
            <div className="flex max-h-96 flex-col divide-y divide-neutral-mid-gray overflow-y-auto">
              {connected.map((link) => (
                <div key={link.id} className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 items-center gap-4">
                    <ClientAvatar name={link.party.name} avatar={link.party.avatar} size="md" />
                    <div className="min-w-0">
                      <Typography variant="action" className="block truncate font-semibold text-neutral-black">
                        {link.party.name}
                      </Typography>
                      <Typography variant="paragraph" className="block truncate text-neutral-silver-gray">
                        {link.party.email}
                        {formatDate(link.grantedAt ?? link.createdAt) && (
                          <span> • Access since {formatDate(link.grantedAt ?? link.createdAt)}</span>
                        )}
                      </Typography>
                    </div>
                  </div>
                  <Button
                    variant="custom"
                    size="sm"
                    className="shrink-0 border border-danger !text-danger hover:bg-danger-light"
                    onClick={() => setRemoveLink(link)}
                  >
                    Remove Access
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Approve/decline works in both views; ReviewRequestModal threads the scoped accountId. */}
      <ReviewRequestModal link={reviewLink} open={!!reviewLink} onClose={() => setReviewLink(null)} />
      <RemoveAssistantModal link={removeLink} open={!!removeLink} onClose={() => setRemoveLink(null)} />
    </div>
  );
};
